import React from 'react';
import type { StatusVariant } from './StatusBadge';

export type TagChipMode = 'static' | 'toggle' | 'link';

export type TagChipProps = {
  label: React.ReactNode;
  mode?: TagChipMode;
  selected?: boolean;
  disabled?: boolean;
  href?: string;
  onClick?: () => void;
  title?: string;
  'aria-label'?: string;
  variant?: StatusVariant;
  className?: string;
};

function chipClassName(
  mode: TagChipMode,
  selected: boolean,
  disabled: boolean,
  variant: StatusVariant | undefined,
  className: string,
): string {
  return [
    'source-chip',
    mode === 'toggle' ? 'source-chip--button' : 'source-chip--static',
    mode === 'toggle' && selected ? 'source-chip--selected' : '',
    disabled ? 'source-chip--disabled' : '',
    variant ? `tag-chip--${variant}` : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
}

/** Reusable pill chip for sources, tags, and toggle selections. Uses shared source-chip styling. */
export default function TagChip({
  label,
  mode = 'static',
  selected = false,
  disabled = false,
  href,
  onClick,
  title,
  'aria-label': ariaLabel,
  variant,
  className = '',
}: TagChipProps) {
  const classes = chipClassName(mode, selected, disabled, variant, className);

  if (mode === 'link' && href) {
    return (
      <a className={classes} href={href} title={title} rel="noopener noreferrer">
        {label}
      </a>
    );
  }

  if (mode === 'toggle') {
    return (
      <button
        type="button"
        className={classes}
        aria-pressed={selected}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={onClick}
        title={title}
      >
        {label}
      </button>
    );
  }

  return (
    <span className={classes} title={title} role="listitem">
      {label}
    </span>
  );
}
